import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Search, Power, Mail, KeyRound, RefreshCw, Copy,
  CheckCircle2, Clock, Lock, XCircle, ChevronRight, Sparkles,
  Smartphone, AlertCircle, LayoutGrid,
} from "lucide-react";
import DemoVisitsWidget from "@/components/admin/DemoVisitsWidget";
import ImpersonateButton from "@/components/admin/ImpersonateButton";
import PendingResetsPanel from "./PendingResetsPanel";

const STATUS_BADGES = {
  actif:             { label: "Actif",            cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  jamais_configure:  { label: "Jamais configuré", cls: "bg-gray-100 text-gray-600",       Icon: Clock },
  verrouille:        { label: "Verrouillé",       cls: "bg-rose-100 text-rose-700",       Icon: Lock },
  desactive:         { label: "Désactivé",        cls: "bg-amber-100 text-amber-700",     Icon: XCircle },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch { return iso; }
}

export default function AccountsTab({ masterByClient = {}, emptyTilesByClient = {}, onOpenAccess }) {
  const [data, setData] = useState({ items: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [credModal, setCredModal] = useState(null); // {username, password, client}
  const [showOnlyIncomplete, setShowOnlyIncomplete] = useState(false);
  const [showOnlyEmptyPortal, setShowOnlyEmptyPortal] = useState(false);

  // Portail actif mais aucune tuile cochée → le client voit une page vide
  const isEmptyPortal = (c) => c.portal_status === "actif" && !!emptyTilesByClient[c.id];

  // Batch #14.206 — Détection fiche incomplète (utilitaire)
  const getMissing = (c) => {
    const m = [];
    if (!c.email) m.push("email");
    if (!c.phone) m.push("téléphone");
    if (c.portal_status === "jamais_configure") m.push("portail non activé");
    return m;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/clients-portal");
      setData(r.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let items = data.items;
    if (showOnlyIncomplete) {
      items = items.filter((c) => getMissing(c).length > 0);
    }
    if (showOnlyEmptyPortal) {
      items = items.filter(isEmptyPortal);
    }
    if (!q) return items;
    return items.filter(
      (c) =>
        (c.nom || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.portal_username || "").toLowerCase().includes(q)
    );
  }, [data.items, search, showOnlyIncomplete, showOnlyEmptyPortal, emptyTilesByClient]);

  // Batch #14.206 — Compteur global des fiches incomplètes
  const incompleteCount = useMemo(
    () => (data.items || []).filter((c) => getMissing(c).length > 0).length,
    [data.items],
  );
  const emptyPortalCount = useMemo(
    () => (data.items || []).filter((c) => c.portal_status === "actif" && !!emptyTilesByClient[c.id]).length,
    [data.items, emptyTilesByClient],
  );

  const handleActivate = async (client) => {
    setBusyId(client.id);
    try {
      const r = await api.post(`/admin/clients/${client.id}/activate`);
      if (r.data.temporary_password) {
        setCredModal({
          username: r.data.portal_username,
          password: r.data.temporary_password,
          client: client.nom,
          title: "Compte créé",
        });
      } else {
        toast.success("Portail activé");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'activation");
    } finally {
      setBusyId(null);
    }
  };

  const handleSendAccess = async (client) => {
    setBusyId(client.id);
    try {
      const r = await api.post(`/admin/clients/${client.id}/send-access`);
      if (r.data.ok) {
        toast.success(`Email envoyé à ${client.email}`);
      } else {
        toast.error("Email non envoyé (vérifier SMTP)");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setBusyId(null);
    }
  };

  const handleResetPassword = async (client) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${client.nom} ?`)) return;
    setBusyId(client.id);
    try {
      const r = await api.post(`/admin/clients/${client.id}/reset-password`);
      setCredModal({
        username: r.data.portal_username,
        password: r.data.temporary_password,
        client: client.nom,
        title: "Mot de passe réinitialisé",
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de réinitialisation");
    } finally {
      setBusyId(null);
    }
  };

  // Batch #14.132 — Resync compte démo (mot de passe figé à "demo123")
  const [resyncingDemo, setResyncingDemo] = useState(false);
  const handleResyncDemo = async () => {
    if (!window.confirm(
      "Resynchroniser le compte démo portail ?\n\n" +
      "Le mot de passe sera réinitialisé à 'demo123'.\n" +
      "Action limitée aux comptes marqués is_demo=true."
    )) return;
    setResyncingDemo(true);
    try {
      const r = await api.post("/admin/portal/demo/resync");
      const usernames = (r.data.accounts || []).map((a) => a.username).join(", ");
      setCredModal({
        username: usernames || "(plusieurs)",
        password: r.data.password,
        client: `Compte(s) démo · ${r.data.count} mis à jour`,
        title: "Démo resynchronisé",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de resync");
    } finally {
      setResyncingDemo(false);
    }
  };

  const totals = data.totals || {};

  return (
    <div data-testid="admin-portal-accounts-page">
      <div className="flex items-center justify-end mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={handleResyncDemo}
            disabled={resyncingDemo}
            className="px-3 py-2 rounded-lg text-xs font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-50 transition inline-flex items-center gap-1.5"
            data-testid="resync-demo-btn"
            title="Force le compte démo portail à 'demo123' (utile post-déploiement)"
          >
            <Sparkles className={`w-3.5 h-3.5 ${resyncingDemo ? "animate-pulse" : ""}`} />
            Resync démo
          </button>
          <button
            onClick={load}
            className="p-2 rounded-lg hover:bg-gray-100 transition"
            aria-label="Rafraîchir"
            data-testid="accounts-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Accès envoyés et jamais utilisés (pastille Cabinet › Portail clients) */}
      <PendingResetsPanel />

      {/* Batch #14.147 — Widget visites portail démo public */}
      <div className="mb-4">
        <DemoVisitsWidget />
      </div>

      {/* Totaux compacts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {Object.entries(STATUS_BADGES).map(([key, b]) => (
          <div
            key={key}
            className={`px-3 py-2 rounded-lg ${b.cls} flex items-center justify-between`}
            data-testid={`accounts-total-${key}`}
          >
            <span className="text-xs font-medium">{b.label}</span>
            <span className="text-lg font-bold">{totals[key] ?? 0}</span>
          </div>
        ))}
      </div>

      {/* Recherche + filtre incomplet */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un client (nom, email, identifiant)..."
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="accounts-search-input"
          />
        </div>
        {/* Batch #14.206 — Filtre fiches incomplètes */}
        <button
          type="button"
          onClick={() => setShowOnlyIncomplete((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border transition ${
            showOnlyIncomplete
              ? "bg-amber-100 text-amber-800 border-amber-300"
              : "bg-white text-slate-700 border-gray-200 hover:bg-amber-50"
          }`}
          title="Afficher uniquement les clients dont la fiche est incomplète (email, téléphone ou portail manquant)"
          data-testid="accounts-toggle-incomplete"
        >
          <AlertCircle className={`w-4 h-4 ${showOnlyIncomplete ? "text-amber-700" : "text-amber-500"}`} />
          {showOnlyIncomplete ? "Fiches incomplètes uniquement" : "Voir fiches incomplètes"}
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
            showOnlyIncomplete ? "bg-amber-200 text-amber-900" : "bg-amber-100 text-amber-700"
          }`}>{incompleteCount}</span>
        </button>
        {/* Portail actif sans aucune tuile cochée */}
        <button
          type="button"
          onClick={() => setShowOnlyEmptyPortal((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border transition ${
            showOnlyEmptyPortal
              ? "bg-rose-100 text-rose-800 border-rose-300"
              : emptyPortalCount > 0
                ? "bg-white text-rose-700 border-rose-200 hover:bg-rose-50"
                : "bg-white text-slate-500 border-gray-200 hover:bg-slate-50"
          }`}
          title="Clients dont le portail est actif mais dont aucune tuile n'est cochée — ils voient une page vide"
          data-testid="accounts-toggle-empty-portal"
        >
          <LayoutGrid className={`w-4 h-4 ${emptyPortalCount > 0 ? "text-rose-600" : "text-slate-400"}`} />
          {showOnlyEmptyPortal ? "Portails vides uniquement" : "Portails vides"}
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
            showOnlyEmptyPortal ? "bg-rose-200 text-rose-900" : emptyPortalCount > 0 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-500"
          }`} data-testid="accounts-empty-portal-count">{emptyPortalCount}</span>
        </button>
      </div>

      {emptyPortalCount > 0 && !showOnlyEmptyPortal && (
        <div className="mb-4 flex items-center gap-2 text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded-lg px-3 py-2" data-testid="accounts-empty-portal-alert">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">
            <b>{emptyPortalCount}</b> client(s) ont un portail <b>actif</b> mais <b>aucune tuile cochée</b> : ils voient « Votre portail n'est pas encore configuré ».
          </span>
          <button type="button" onClick={() => setShowOnlyEmptyPortal(true)} className="underline font-semibold" data-testid="accounts-empty-portal-alert-show">
            Voir
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="accounts-table">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Nom</th>
                <th className="text-left px-4 py-3 font-medium">Statut</th>
                <th className="text-left px-4 py-3 font-medium">Dernière connexion</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                    Chargement...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-400" data-testid="accounts-empty">
                    {search ? "Aucun client correspondant" : "Aucun client"}
                  </td>
                </tr>
              )}
              {!loading && filtered.map((c) => {
                const status = STATUS_BADGES[c.portal_status] || STATUS_BADGES.jamais_configure;
                const StatusIcon = status.Icon;
                const isBusy = busyId === c.id;
                const missing = getMissing(c);
                return (
                  <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50 group" data-testid={`account-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/portal-admin?client_id=${c.id}`}
                        className="block group/link"
                        data-testid={`account-open-${c.id}`}
                        title="Ouvrir la fiche d'accès portail"
                      >
                        <div className="font-medium text-gray-900 group-hover/link:text-cyan-700 transition inline-flex items-center gap-1 flex-wrap">
                          {c.nom}
                          {/* Batch #14.206 — Badge fiche incomplète */}
                          {missing.length > 0 && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded border border-amber-300"
                              data-testid={`account-incomplete-${c.id}`}
                              title={`Fiche incomplète — manque : ${missing.join(", ")}`}
                            >
                              <AlertCircle className="w-2.5 h-2.5" /> Fiche incomplète
                            </span>
                          )}
                          {/* Portail actif sans tuile */}
                          {isEmptyPortal(c) && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] font-semibold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded border border-rose-300"
                              data-testid={`account-empty-portal-${c.id}`}
                              title="Portail actif mais aucune tuile cochée — le client voit une page vide"
                            >
                              <LayoutGrid className="w-2.5 h-2.5" /> Portail vide
                            </span>
                          )}
                          {/* Batch #14.189 — Badge SMS opt-in client */}
                          {c.has_sms_enabled ? (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200"
                              data-testid={`account-sms-badge-on-${c.id}`}
                              title="Ce client a activé les notifications SMS dans ses préférences"
                            >
                              <Smartphone className="w-2.5 h-2.5" /> SMS ON
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] font-semibold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200"
                              data-testid={`account-sms-badge-off-${c.id}`}
                              title={c.phone ? "Notifications SMS désactivées" : "Pas de téléphone — SMS impossible"}
                            >
                              <Smartphone className="w-2.5 h-2.5" /> SMS OFF
                            </span>
                          )}
                          <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover/link:text-cyan-600 group-hover/link:translate-x-0.5 transition-all" />
                        </div>
                        <div className="text-xs text-gray-500">{c.email || "—"}</div>
                        {c.portal_username && (
                          <div className="text-[11px] text-gray-400 font-mono">@{c.portal_username}</div>
                        )}
                        {/* Batch #14.206 — Détail des champs manquants pour aider à compléter rapidement */}
                        {missing.length > 0 && (
                          <div className="text-[10px] text-amber-700 mt-0.5">
                            Manque : <span className="font-semibold">{missing.join(" · ")}</span>
                          </div>
                        )}
                      </Link>
                      {masterByClient[c.id] && onOpenAccess && (
                        <button
                          type="button"
                          onClick={() => onOpenAccess(masterByClient[c.id])}
                          className="mt-1 inline-flex items-center gap-1 text-[11px] text-rose-700 hover:underline"
                          title="Configurer les tuiles visibles sur le portail de ce dossier"
                          data-testid={`account-access-${c.id}`}
                        >
                          <LayoutGrid className="w-3 h-3" /> Accès aux tuiles
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${status.cls}`}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                      {c.must_change_password && (
                        <div className="text-[10px] text-amber-600 mt-1">⚠ MDP à changer</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {c.last_login_at ? fmtDate(c.last_login_at) : <span className="text-gray-400">Jamais</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {c.portal_status !== "actif" && (
                          <button
                            onClick={() => handleActivate(c)}
                            disabled={isBusy}
                            className="px-2.5 py-1.5 rounded-md text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition flex items-center gap-1"
                            data-testid={`activate-btn-${c.id}`}
                          >
                            <Power className="w-3 h-3" /> Activer
                          </button>
                        )}
                        <button
                          onClick={() => handleSendAccess(c)}
                          disabled={isBusy || !c.email}
                          title={!c.email ? "Aucun email enregistré" : ""}
                          className="px-2.5 py-1.5 rounded-md text-xs bg-cyan-50 text-cyan-700 hover:bg-cyan-100 disabled:opacity-50 transition flex items-center gap-1"
                          data-testid={`send-access-btn-${c.id}`}
                        >
                          <Mail className="w-3 h-3" /> Envoyer accès
                        </button>
                        {c.portal_status === "actif" && (
                          <button
                            onClick={() => handleResetPassword(c)}
                            disabled={isBusy}
                            className="px-2.5 py-1.5 rounded-md text-xs bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50 transition flex items-center gap-1"
                            data-testid={`reset-pw-btn-${c.id}`}
                          >
                            <KeyRound className="w-3 h-3" /> Reset MDP
                          </button>
                        )}
                        {/* Batch #14.338 — « Naviguer en tant que » (administrateur principal, 15 min, journalisé) */}
                        {c.portal_status === "actif" && <ImpersonateButton client={{ ...c, portal_enabled: true }} compact />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal credentials */}
      {credModal && <CredentialsModal {...credModal} onClose={() => setCredModal(null)} />}
    </div>
  );
}

function CredentialsModal({ title, username, password, client, onClose }) {
  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copié`);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="credentials-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500">{client}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="text-[11px] uppercase text-gray-500 mb-1">Identifiant</div>
            <div className="flex items-center justify-between">
              <code className="text-sm font-mono text-gray-900">{username}</code>
              <button onClick={() => copy(username, "Identifiant")} className="p-1.5 hover:bg-white rounded transition" data-testid="copy-username-btn">
                <Copy className="w-3.5 h-3.5 text-gray-500" />
              </button>
            </div>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
            <div className="text-[11px] uppercase text-amber-700 mb-1">Mot de passe temporaire</div>
            <div className="flex items-center justify-between">
              <code className="text-sm font-mono text-amber-900 select-all">{password}</code>
              <button onClick={() => copy(password, "Mot de passe")} className="p-1.5 hover:bg-white rounded transition" data-testid="copy-password-btn">
                <Copy className="w-3.5 h-3.5 text-amber-700" />
              </button>
            </div>
            <p className="text-[11px] text-amber-700 mt-2">
              Le client devra le changer à sa première connexion.
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full mt-5 px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition"
          data-testid="credentials-close-btn"
        >
          Fermer
        </button>
      </div>
    </div>
  );
}
